import React, { useState, useEffect, useMemo } from 'react';
import axios from '../api/axios';
import { toast } from 'react-toastify';
import ProductList from '../components/ProductList';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/products');
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  // Derive category chips from whatever data the API actually returns —
  // works whether or not products have a `category` field.
  const categories = useMemo(() => {
    const unique = new Set(
      products.map((p) => p.category).filter(Boolean)
    );
    return ['all', ...Array.from(unique)];
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchesCategory =
        activeCategory === 'all' || p.category === activeCategory;
      const matchesSearch =
        search.trim() === '' ||
        p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.description?.toLowerCase().includes(search.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [products, search, activeCategory]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-6">
        <div className="max-w-5xl mx-auto animate-pulse flex flex-col gap-6">
          <div className="flex flex-col gap-2 mb-4">
            <div className="h-3 bg-gray-200 rounded w-16" />
            <div className="h-8 bg-gray-200 rounded w-52" />
          </div>
          <div className="h-10 bg-gray-200 rounded-xl w-full mb-2" />
          <div className="flex gap-2 mb-6">
            <div className="h-8 bg-gray-200 rounded-full w-24" />
            <div className="h-8 bg-gray-200 rounded-full w-24" />
            <div className="h-8 bg-gray-200 rounded-full w-24" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            <div className="h-64 bg-white border border-gray-100 rounded-2xl" />
            <div className="h-64 bg-white border border-gray-100 rounded-2xl" />
            <div className="h-64 bg-white border border-gray-100 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-6 py-10">

        {/* Header */}
        <div className="mb-8">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">Shop</p>
          <h1 className="text-2xl font-bold text-gray-900">Our products</h1>
          <p className="text-sm text-gray-500 mt-1">
            Genuine printer parts, supplies, and accessories.
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <svg
            className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products…"
            className="w-full bg-white border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          />
        </div>

        {/* Category chips */}
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`text-xs font-medium px-3.5 py-1.5 rounded-full border transition capitalize ${
                  activeCategory === cat
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {cat === 'all' ? 'All products' : cat}
              </button>
            ))}
          </div>
        )}

        {/* Results count */}
        <p className="text-xs text-gray-400 mb-4">
          {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
          {search && <> matching "<span className="text-gray-600">{search}</span>"</>}
        </p>

        <ProductList products={filteredProducts} />
      </div>
    </div>
  );
};

export default Products;